import type { PushProduct, TriedAction } from "./data"
import { TODAY, addDays, daysBetween, formatDate, shortDate } from "./dates"
import { SALES_WEEKS } from "./sheetData"

// Sold and gifted units for the graphs. The sheet has weekly totals for the
// 12 weeks from Aug 2; each week is spread over its days with a fixed wobble,
// so the Day graph is made up from the weekly figure.
//
// Day shows up to the last 90 days, Week up to the last 26 weeks, Month the
// calendar months (this month so far). The sheet only goes back to Aug 2.

export type Grain = "day" | "week" | "month"

const HISTORY_START = SALES_WEEKS[0]

// A past action with its number on the graphs: 1 for the oldest, counting up
// by date, so the same action keeps its number on Day, Week and Month.
export interface NumberedAction extends TriedAction {
  n: number
}

export function pastActions(product: PushProduct): NumberedAction[] {
  return [...product.tried].sort((a, b) => a.date.localeCompare(b.date)).map((a, i) => ({ ...a, n: i + 1 }))
}

// What every graph point has, whatever it measures.
export interface BasePoint {
  i: number // 0 = oldest
  tick: string // label on the date axis
  title: string // heading in the tooltip
  actions: NumberedAction[]
}

export interface Point extends BasePoint {
  sold: number
  gifted: number
}

export interface Day {
  date: string // ISO date
  sold: number
  gifted: number
  actions: NumberedAction[]
}

function wobble(seed: string, day: number) {
  const n = [...seed].reduce((sum, c) => sum + c.charCodeAt(0), 0)
  const x = Math.sin(n * 12.9898 + day * 78.233) * 43758.5453
  return (x - Math.floor(x) - 0.5) * 0.6
}

export function dailySales(product: PushProduct): Day[] {
  const total = daysBetween(HISTORY_START, TODAY) + 1
  const numbered = pastActions(product)
  const lastWeek = SALES_WEEKS.length - 1

  return Array.from({ length: total }, (_, day) => {
    const date = addDays(HISTORY_START, day)
    const week = Math.min(Math.floor(day / 7), lastWeek)
    // The last week only runs to today.
    const daysInWeek = week === lastWeek ? daysBetween(SALES_WEEKS[lastWeek], TODAY) + 1 : 7
    const spread = (weekly: number[], seed: string) =>
      Math.max(0, Math.round((weekly[week] / daysInWeek) * (1 + wobble(seed, day))))
    return {
      date,
      sold: spread(product.weeklySold, product.sku),
      gifted: spread(product.weeklyGifted, product.sku + "g"),
      actions: numbered.filter((a) => a.date === date),
    }
  })
}

export interface Bucket<T> {
  tick: string
  title: string
  days: T[]
}

// Past days grouped for the chosen grain: up to the last 90 days, up to the
// last 26 weeks ending today, or calendar months with this month marked "so far".
export function pastBuckets<T extends { date: string }>(days: T[], grain: Grain): Bucket<T>[] {
  if (grain === "day") {
    return days.slice(-90).map((d) => ({ tick: shortDate(d.date), title: shortDate(d.date), days: [d] }))
  }

  if (grain === "week") {
    const count = Math.min(26, Math.floor(days.length / 7))
    const recent = days.slice(-count * 7)
    return Array.from({ length: count }, (_, i) => {
      const week = recent.slice(i * 7, i * 7 + 7)
      const first = week[0].date
      return { tick: shortDate(first), title: `${shortDate(first)} – ${shortDate(week[week.length - 1].date)}`, days: week }
    })
  }

  const months = new Map<string, T[]>()
  for (const d of days) {
    const key = d.date.slice(0, 7)
    months.set(key, [...(months.get(key) ?? []), d])
  }
  return [...months.entries()].map(([key, list]) => {
    const first = `${key}-01`
    const soFar = key === TODAY.slice(0, 7) ? " (so far)" : ""
    return {
      tick: formatDate(first, { month: "short" }) + soFar,
      title: formatDate(first, { month: "long", year: "numeric" }) + soFar,
      days: list,
    }
  })
}

export function salesSeries(product: PushProduct, grain: Grain): Point[] {
  return pastBuckets(dailySales(product), grain).map((b, i) => ({
    i,
    tick: b.tick,
    title: b.title,
    sold: b.days.reduce((sum, d) => sum + d.sold, 0),
    gifted: b.days.reduce((sum, d) => sum + d.gifted, 0),
    actions: b.days.flatMap((d) => d.actions),
  }))
}
